/**
 * useFormattedDate Hook
 * 
 * React hook for formatting dates with automatic locale updates.
 * Re-renders when the i18n language changes.
 */

import { useMemo } from 'react';
import { useTranslation } from '@/core';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { useLocation } from '@/entities/location';
import type { DateFormatPref, TimeFormatPref } from '@/entities/location';
import { formatDate, formatRelativeDate, formatDistanceDate, DATE_FORMATS, DateFormatKey } from '../utils/dateFormat';

/** Location date-format preference → date-fns numeric pattern. */
const DATE_PATTERN: Record<DateFormatPref, string> = {
  'DD/MM/YYYY': 'dd/MM/yyyy',
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
};

/** Location time-format preference → date-fns time pattern. */
const TIME_PATTERN: Record<TimeFormatPref, string> = {
  '24h': 'HH:mm',
  '12h': 'h:mm a',
};

/**
 * Hook that provides locale-aware date formatting functions.
 * Automatically updates when the language changes.
 * 
 * @example
 * const { format, formatRelative } = useFormattedDate();
 * 
 * // Use in component
 * <span>{format(booking.checkInDate, 'FULL_DATE')}</span>
 * <span>{formatRelative(notification.createdAt)}</span>
 */
export function useFormattedDate() {
  const { i18n } = useTranslation();
  const { activeLocationId } = useActiveContext();
  const { data: activeLocation } = useLocation(activeLocationId ?? undefined);
  const dateFormat = activeLocation?.dateFormat ?? 'DD/MM/YYYY';
  const timeFormat = activeLocation?.timeFormat ?? '24h';

  // Re-create formatters when language or the location's format prefs change
  return useMemo(() => {
    // The location's explicit numeric/time prefs override the locale-derived
    // presets; month-name presets (FULL/MEDIUM) stay language-driven.
    const overrides: Partial<Record<DateFormatKey, string>> = {
      SHORT_DATE: DATE_PATTERN[dateFormat],
      TIME: TIME_PATTERN[timeFormat],
      DATETIME: `${DATE_PATTERN[dateFormat]} ${TIME_PATTERN[timeFormat]}`,
    };
    return {
    /**
     * Format a date with the current locale + location format prefs
     * @param date - Date to format
     * @param formatKey - Either a DATE_FORMATS key or a custom format string
     */
    format: (date: Date | number | string | null | undefined, formatKey: DateFormatKey | string): string => {
      const formatStr = overrides[formatKey as DateFormatKey] ?? DATE_FORMATS[formatKey as DateFormatKey] ?? formatKey;
      return formatDate(date, formatStr);
    },
    
    /**
     * Format a date relative to now
     */
    formatRelative: (date: Date | number | string, baseDate?: Date): string => {
      return formatRelativeDate(date, baseDate);
    },
    
    /**
     * Format distance between dates
     */
    formatDistance: (
      date: Date | number | string, 
      baseDate?: Date, 
      options?: { addSuffix?: boolean; includeSeconds?: boolean }
    ): string => {
      return formatDistanceDate(date, baseDate, options);
    },
    
    /**
     * Available format presets
     */
    formats: DATE_FORMATS,
    };
  }, [i18n.language, dateFormat, timeFormat]); // eslint-disable-line react-hooks/exhaustive-deps
}

export default useFormattedDate;
